import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

// White dots on a thin dark line. The dark line keeps the dots visible on
// bright imagery, such as snow or a road, and the dots stay visible on dark
// imagery, such as a forest.
const dotColor = 'rgba(255, 255, 255, 0.95)';
const underlineColor = 'rgba(0, 0, 0, 0.45)';
const dotLength = '4px';
const gapLength = '8px';

const CrosshairLayer = styled.div`
	position: absolute;
	inset: 0;
	overflow: hidden;
	pointer-events: none;
	z-index: 1;
	opacity: 0;
	transition: opacity 100ms linear;

	&.is-visible {
		opacity: 1;
	}
`;

const Line = styled.div`
	position: absolute;
	top: 0;
	left: 0;
	background-color: ${underlineColor};
	will-change: transform;
`;

const VerticalLine = styled(Line)`
	width: 1px;
	height: 100%;
	background-image: repeating-linear-gradient(
		to bottom,
		${dotColor} 0 ${dotLength},
		transparent ${dotLength} ${gapLength}
	);
`;

const HorizontalLine = styled(Line)`
	width: 100%;
	height: 1px;
	background-image: repeating-linear-gradient(
		to right,
		${dotColor} 0 ${dotLength},
		transparent ${dotLength} ${gapLength}
	);
`;

/**
 * Draws a dotted crosshair that follows the cursor on the map.
 *
 * The crosshair shows where the annotation box starts and ends. This helps the
 * user line up a box with an object that is far from the cursor.
 *
 * The component writes to the DOM directly on each pointer move. React state
 * would re-render the map view on every mouse event.
 *
 * @param {Object} map - the OpenLayers map to draw the crosshair on
 * @param {Boolean} active - false hides the crosshair, for example when the
 * user erases instead of draws
 */
const AnnotationCrosshair = ({ map, active = true }) => {
	const layerRef = useRef(null);
	const verticalRef = useRef(null);
	const horizontalRef = useRef(null);

	useEffect(() => {
		if (!map || !active) return;

		const layer = layerRef.current;
		const vertical = verticalRef.current;
		const horizontal = horizontalRef.current;
		if (!layer || !vertical || !horizontal) return;

		const viewport = map.getViewport();

		const hide = () => layer.classList.remove('is-visible');

		const onPointerMove = event => {
			// A touch pointer has no hover position. The lines would stay behind
			// after the finger leaves the screen.
			if (event.originalEvent?.pointerType === 'touch') {
				hide();
				return;
			}

			const [x, y] = event.pixel;
			vertical.style.transform = `translate3d(${x}px, 0, 0)`;
			horizontal.style.transform = `translate3d(0, ${y}px, 0)`;
			layer.classList.add('is-visible');
		};

		// A touch tap can start without a pointer move. Hide the crosshair at
		// once, or the lines stay at the last position of the mouse.
		const onPointerDown = event => {
			if (event.pointerType === 'touch') hide();
		};

		map.on('pointermove', onPointerMove);
		viewport.addEventListener('pointerdown', onPointerDown);
		viewport.addEventListener('pointerleave', hide);

		return () => {
			map.un('pointermove', onPointerMove);
			viewport.removeEventListener('pointerdown', onPointerDown);
			viewport.removeEventListener('pointerleave', hide);
		};
	}, [map, active]);

	if (!map || !active) return null;

	return createPortal(
		<CrosshairLayer ref={layerRef} aria-hidden="true">
			<VerticalLine ref={verticalRef} />
			<HorizontalLine ref={horizontalRef} />
		</CrosshairLayer>,
		map.getViewport()
	);
};

export default AnnotationCrosshair;
